import React, { useState, useEffect } from 'react';
import { Check, X, Zap } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import './ProUpgradeModal.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const ProUpgradeModal = ({ isOpen, onClose, onUpgrade, currentBillingInterval = null }) => {
  const { getToken } = useAuth();
  const { username } = useRevit();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Derive a friendly label for the user's current billing cadence. The
  // upgrade endpoint keys on the same 'month' | 'year' values, so we keep
  // them intact and only translate at the UI boundary.
  const intervalLabel = currentBillingInterval === 'year'
    ? 'annual'
    : currentBillingInterval === 'month'
      ? 'monthly'
      : null;

  // Canonical Pro price for the matching billing cadence. Hardcoded so
  // the modal always shows the public-facing figures ($149 / month or
  // $1,192 / year) regardless of what Stripe happens to have stored.
  // Defaults to the monthly price when the user has no active sub.
  const proPriceText = currentBillingInterval === 'year'
    ? '$1,192 a year'
    : '$149 a month';

  const handleUpgrade = async () => {
    setError('');
    setIsUpgrading(true);
    try {
      const token = await getToken();
      await axios.post(
        `https://${SERVER_URL}/api/upgrade-to-pro`,
        { userId: username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Show the celebratory success modal; the page reloads once the user closes it.
      setShowSuccess(true);
    } catch (err) {
      console.error('Error upgrading to Pro:', err);
      setError(err.response?.data?.error || 'Something went wrong upgrading your plan. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  // Fire a confetti explosion whenever the success modal appears.
  useEffect(() => {
    if (!showSuccess) return;

    const duration = 2500;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 35, spread: 360, ticks: 60, zIndex: 100001 };
    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    // Big initial burst
    confetti({ ...defaults, particleCount: 160, origin: { x: 0.5, y: 0.45 } });

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);

    return () => clearInterval(interval);
  }, [showSuccess]);

  // Closing the success modal reloads so the app picks up the new Pro state.
  const handleSuccessClose = () => {
    onUpgrade?.();
  };

  if (!isOpen) return null;

  if (showSuccess) {
    return (
      <div className="pum-overlay" onClick={handleSuccessClose}>
        <div className="pum-modal pum-success-modal" onClick={(e) => e.stopPropagation()}>
          <button className="pum-close-btn" onClick={handleSuccessClose} aria-label="Close modal">
            <X size={20} />
          </button>

          <div className="pum-success-content">
            <div className="pum-success-icon">
              <Check size={44} strokeWidth={3} />
            </div>
            <h2 className="pum-title">You're now on RevTrack Pro! 🎉</h2>
            <p className="pum-subtitle">
              Your account has been upgraded. Enjoy unlimited offers, urgency links, and every Pro feature.
            </p>
            <button className="pum-upgrade-btn" onClick={handleSuccessClose}>
              Let's go
            </button>
          </div>
        </div>
      </div>
    );
  }

  const benefits = [
    "Track Unlimited Offers/Sign Up Pages",
    "One-Click Channel-Wide Link Updates",
    "Identify Viewer Conversion Paths",
    "Performance Dashboard",
    "Urgency Links",
    "Automated Viewer Comment Analysis",
    "AI Video Ideas Optimised For Conversion",
    "Add Team Members"
  ];

  return (
    <div className="pum-overlay" onClick={onClose}>
      <div className="pum-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pum-close-btn" onClick={onClose} aria-label="Close modal">
          <X size={20} />
        </button>

        <div className="pum-header">
          <div className="pum-icon-wrap">
            <Zap size={32} fill="var(--Pro-Purple)" color="var(--Pro-Purple)" />
          </div>
          <h2 className="pum-title">Upgrade to Revtrack Pro</h2>
          <p className="pum-subtitle">Unlock powerful features to scale your revenue tracking</p>
          {intervalLabel && (
            <p className="pum-current-plan">
              You're currently on the {intervalLabel} plan — you'll be upgraded to the {intervalLabel} Pro plan.
            </p>
          )}
        </div>

        <ul className="pum-benefits-list">
          {benefits.map((benefit, index) => (
            <li key={index} className="pum-benefit-item">
              <div className="pum-benefit-check">
                <Check size={14} strokeWidth={3} />
              </div>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>

        <div className="pum-footer">
          <div className="pum-prominent-price">
            <span className="pum-price-label">Pro Plan</span>
            <span className="pum-price-value">{proPriceText}</span>
          </div>

          <button className="pum-upgrade-btn" onClick={handleUpgrade} disabled={isUpgrading}>
            {isUpgrading ? 'Upgrading…' : 'Upgrade Now'}
          </button>

          <div className="pum-pricing-summary">
            <p className="pum-charge-now">
              You will be charged a prorated amount today, and then{' '}
              <strong>{proPriceText}</strong>
            </p>
            <p className="pum-cancel-note">Cancel anytime</p>
          </div>

          {error && <p className="pum-error">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default ProUpgradeModal;
